import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Smartphone, Bell, Shuffle, Check, ChevronDown, ChevronUp, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { subscribeToPush, isPushSubscribed } from "@/lib/pushManager";

// ===== Helper: detect PWA installed state =====
// "Standalone" display mode is the reliable sign the user launched us from the home screen icon.
const detectPwaInstalled = () => {
  if (typeof window === "undefined") return false;
  const isStandalone =
    window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  // iOS Safari specific
  const iosStandalone = window.navigator?.standalone === true;
  return Boolean(isStandalone || iosStandalone);
};

const detectPlatform = () => {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
};

// ===== Step card shared UI =====
const StepCard = ({ number, title, description, status, icon: Icon, expanded, onToggle, children }) => {
  const isDone = status === "done";
  return (
    <div
      className={`rounded-xl border-2 overflow-hidden transition-colors ${
        isDone ? "border-green-300 bg-green-50/40" : "border-rose-200 bg-white"
      }`}
      data-testid={`setup-step-${number}`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-3 text-left"
      >
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
            isDone ? "bg-green-500 text-white" : "bg-rose-500 text-white"
          }`}
        >
          {isDone ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-rose-500">
            Step {number} · {isDone ? "Completato" : "Da fare"}
          </p>
          <p className="text-sm font-semibold">{title}</p>
          <p className="text-xs text-muted-foreground truncate">{description}</p>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-rose-100">{children}</div>
      )}
    </div>
  );
};

// ===== Step 1: Install PWA =====
const InstallPwaStep = ({ platform }) => {
  if (platform === "ios") {
    return (
      <div className="space-y-2 text-sm">
        <p className="font-medium">Su iPhone (Safari):</p>
        <ol className="list-decimal list-inside space-y-1 text-xs text-muted-foreground">
          <li>Tocca il tasto Condividi <span className="inline-block">⬆</span> in basso</li>
          <li>Scorri e tocca <strong>"Aggiungi alla schermata Home"</strong></li>
          <li>Conferma toccando <strong>"Aggiungi"</strong> in alto a destra</li>
          <li>Apri l'app dall'icona sulla schermata Home (non dal browser)</li>
        </ol>
        <div className="flex items-start gap-2 p-2 mt-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-900">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Su iOS le notifiche push funzionano solo con iOS 16.4+ e quando l'app viene aperta dall'icona installata.</span>
        </div>
      </div>
    );
  }
  if (platform === "android") {
    return (
      <div className="space-y-2 text-sm">
        <p className="font-medium">Su Android (Chrome):</p>
        <ol className="list-decimal list-inside space-y-1 text-xs text-muted-foreground">
          <li>Tocca il menu <span className="inline-block">⋮</span> in alto a destra</li>
          <li>Scegli <strong>"Installa app"</strong> o <strong>"Aggiungi a schermata Home"</strong></li>
          <li>Conferma toccando <strong>"Installa"</strong></li>
          <li>L'icona Your Life Map comparirà nel drawer/home screen</li>
        </ol>
      </div>
    );
  }
  return (
    <div className="space-y-2 text-sm">
      <p className="font-medium">Su desktop (Chrome/Edge):</p>
      <ol className="list-decimal list-inside space-y-1 text-xs text-muted-foreground">
        <li>Cerca l'icona <strong>Installa app</strong> nella barra degli indirizzi (piccolo computer con freccia)</li>
        <li>Clicca e conferma l'installazione</li>
      </ol>
      <p className="text-[11px] text-muted-foreground italic mt-1">
        Il vero beneficio delle notifiche si ha sul cellulare, quindi ti consigliamo di ripetere questi step da mobile quando puoi.
      </p>
    </div>
  );
};

// ===== Step 2: Enable Push =====
const EnablePushStep = ({ permission, subscribed, onEnable, busy }) => {
  if (subscribed) {
    return (
      <p className="text-sm text-green-700 flex items-center gap-1.5">
        <Check className="w-4 h-4" />
        Notifiche attive su questo dispositivo. Puoi fare un test dalla card "Notifiche Push".
      </p>
    );
  }
  if (permission === "denied") {
    return (
      <div className="space-y-2 text-sm">
        <div className="flex items-start gap-2 p-2 rounded-lg bg-red-50 border border-red-200 text-[11px] text-red-900">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <strong>Permesso bloccato dal browser.</strong> Va riattivato manualmente:
            <ul className="list-disc list-inside mt-1">
              <li>Tocca l'icona 🔒/ⓘ vicino all'URL → Notifiche → Consenti</li>
              <li>Ricarica la pagina e riprova</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Ti chiederemo il permesso di inviare notifiche. Puoi sempre disattivarle dopo.
      </p>
      <Button
        onClick={onEnable}
        disabled={busy}
        size="sm"
        className="bg-rose-500 hover:bg-rose-600 text-white w-full"
        data-testid="wizard-enable-push-btn"
      >
        {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Attivo...</> : <><Bell className="w-4 h-4 mr-1.5" />Attiva notifiche</>}
      </Button>
    </div>
  );
};

// ===== Step 3: Enable randomized reminders =====
const EnableRemindersStep = ({ enabled, onEnable, busy }) => {
  if (enabled) {
    return (
      <p className="text-sm text-green-700 flex items-center gap-1.5">
        <Check className="w-4 h-4" />
        Promemoria randomizzati attivi. Regola orari/numero nella card sottostante.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Attiveremo 5 prompt casuali al giorno tra le 9:00 e le 21:00 (potrai modificare).
      </p>
      <Button
        onClick={onEnable}
        disabled={busy}
        size="sm"
        className="bg-rose-500 hover:bg-rose-600 text-white w-full"
        data-testid="wizard-enable-reminders-btn"
      >
        {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Attivo...</> : <><Shuffle className="w-4 h-4 mr-1.5" />Attiva promemoria casuali</>}
      </Button>
    </div>
  );
};

// ===== Main wizard =====
export const NotificationsSetupWizard = () => {
  const [hasAccess, setHasAccess] = useState(null);
  const [pwaInstalled, setPwaInstalled] = useState(false);
  const [permission, setPermission] = useState("default");
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [remindersEnabled, setRemindersEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null); // which step is expanded
  const [platform] = useState(detectPlatform());

  const refreshState = useCallback(async () => {
    setPwaInstalled(detectPwaInstalled());
    if (typeof Notification !== "undefined") setPermission(Notification.permission);
    try {
      const subbed = await isPushSubscribed();
      setPushSubscribed(Boolean(subbed));
    } catch {
      setPushSubscribed(false);
    }
    try {
      const res = await api.get("/voice-mood/schedule");
      setRemindersEnabled(Boolean(res.data?.enabled));
    } catch {
      setRemindersEnabled(false);
    }
  }, []);

  useEffect(() => {
    // Check access
    api.get("/voice-mood/access")
      .then((r) => setHasAccess(r.data?.has_access))
      .catch(() => setHasAccess(false));
    refreshState();
    // React to visibility / focus changes (e.g. after installing)
    const handler = () => refreshState();
    window.addEventListener("focus", handler);
    return () => window.removeEventListener("focus", handler);
  }, [refreshState]);

  const handleEnablePush = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      toast.success("Notifiche push attivate!");
      await refreshState();
      setExpanded(3); // auto-advance to step 3
    } catch (e) {
      toast.error(e?.message || "Impossibile attivare le notifiche");
      await refreshState();
    } finally {
      setBusy(false);
    }
  };

  const handleEnableReminders = async () => {
    setBusy(true);
    try {
      await api.post("/voice-mood/schedule", {
        enabled: true,
        mode: "random",
        prompts_per_day: 5,
        start_hour: 9,
        end_hour: 21,
        min_gap_minutes: 60,
        active_weekdays: [0, 1, 2, 3, 4, 5, 6],
        times: [],
      });
      toast.success("Promemoria randomizzati attivati!");
      await refreshState();
      setExpanded(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'attivazione");
    } finally {
      setBusy(false);
    }
  };

  const step1Status = pwaInstalled ? "done" : "pending";
  const step2Status = pushSubscribed ? "done" : "pending";
  const step3Status = remindersEnabled ? "done" : "pending";
  const allDone = pwaInstalled && pushSubscribed && remindersEnabled;

  // Auto-expand first incomplete step on mount
  useEffect(() => {
    if (expanded !== null) return;
    if (!pwaInstalled) setExpanded(1);
    else if (!pushSubscribed) setExpanded(2);
    else if (!remindersEnabled) setExpanded(3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pwaInstalled, pushSubscribed, remindersEnabled]);

  if (hasAccess === false || hasAccess === null) return null;

  return (
    <Card className="border-2 border-rose-200 shadow-sm" data-testid="notifications-wizard-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-semibold text-base flex items-center gap-2">
              <Bell className="w-4 h-4 text-rose-500" />
              Setup promemoria check-in
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              3 step per ricevere 5 promemoria casuali al giorno sul tuo cellulare.
            </p>
          </div>
          {allDone && (
            <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 text-[10px] font-semibold flex items-center gap-1 shrink-0">
              <Check className="w-3 h-3" />
              Tutto attivo
            </span>
          )}
        </div>

        <div className="space-y-2">
          <StepCard
            number={1}
            title="Installa l'app sul telefono"
            description="Aggiungi alla schermata Home per abilitare le push"
            status={step1Status}
            icon={Smartphone}
            expanded={expanded === 1}
            onToggle={() => setExpanded(expanded === 1 ? null : 1)}
          >
            <InstallPwaStep platform={platform} />
          </StepCard>

          <StepCard
            number={2}
            title="Attiva le notifiche"
            description="Dai il permesso al browser"
            status={step2Status}
            icon={Bell}
            expanded={expanded === 2}
            onToggle={() => setExpanded(expanded === 2 ? null : 2)}
          >
            <EnablePushStep
              permission={permission}
              subscribed={pushSubscribed}
              onEnable={handleEnablePush}
              busy={busy}
            />
          </StepCard>

          <StepCard
            number={3}
            title="Attiva promemoria casuali"
            description="5 prompt al giorno in orari casuali"
            status={step3Status}
            icon={Shuffle}
            expanded={expanded === 3}
            onToggle={() => setExpanded(expanded === 3 ? null : 3)}
          >
            <EnableRemindersStep
              enabled={remindersEnabled}
              onEnable={handleEnableReminders}
              busy={busy}
            />
          </StepCard>
        </div>
      </CardContent>
    </Card>
  );
};

export default NotificationsSetupWizard;
